import { config } from "@/config";
import { activeJwt } from "@/auth/session";
import { playUIClick, playNewMail } from "@/audio/SoundManager";

interface InboxMessage {
  id: string;
  kind: string;
  subject: string;
  body: string;
  sender_id: string | null;
  read_at: string | null;
  sent_at: string;
}

export class PlayerList {
  private container = document.getElementById("player-list")!;
  private listEl = document.getElementById("player-list-items")!;
  private panel = document.getElementById("postbox-panel")!;
  private panelRecipient = document.getElementById("postbox-recipient") as HTMLSpanElement;
  private panelSubject = document.getElementById("postbox-subject") as HTMLInputElement;
  private panelBody = document.getElementById("postbox-body") as HTMLTextAreaElement;
  private panelSendBtn = document.getElementById("postbox-send") as HTMLButtonElement;
  private panelCloseBtn = document.getElementById("postbox-close") as HTMLButtonElement;
  private panelStatus = document.getElementById("postbox-status") as HTMLDivElement;

  // Inbox elements
  private postboxBtn = document.getElementById("postbox-btn") as HTMLButtonElement;
  private postboxBadge = document.getElementById("postbox-badge") as HTMLSpanElement;
  private inboxOverlay = document.getElementById("inbox-overlay")!;
  private inboxCloseBtn = document.getElementById("inbox-close-btn") as HTMLButtonElement;
  private inboxMessages = document.getElementById("inbox-messages")!;
  private inboxEmpty = document.getElementById("inbox-empty")!;

  private players = new Map<string, string>(); // user_id → display label
  private musicState = new Map<string, { trackName: string; playlist: string[] }>();
  private voiceActive = new Set<string>(); // user_ids currently broadcasting voice
  private selfId = "";

  setSelfId(id: string): void {
    this.selfId = id;
    // The snapshot/player_joined event for self can arrive before
    // setSelfId is called (channel.on handlers fire as the join push
    // is replied to, which may interleave with the .join() promise).
    // Without this sweep, self leaks into the sidebar until the next
    // snapshot.
    if (this.players.delete(id)) this.renderPlayers();
  }
  private selectedRecipient = "";
  private messages: InboxMessage[] = [];
  private unreadCount = 0;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private expandedMsgId: string | null = null;

  onPlayerMusicClick: ((userId: string, displayName: string) => void) | null = null;

  constructor() {
    this.panelCloseBtn.addEventListener("click", () => this.closeComposePanel());
    this.panelSendBtn.addEventListener("click", () => this.sendMessage());
    this.postboxBtn.addEventListener("click", () => this.toggleInbox());
    this.inboxCloseBtn.addEventListener("click", () => this.closeInbox());

    // Prevent game input while typing in panel
    for (const el of [this.panelSubject, this.panelBody]) {
      el.addEventListener("keydown", ((e: KeyboardEvent) => {
        e.stopPropagation();
        if (e.key === "Escape") this.closeComposePanel();
      }) as EventListener);
    }
  }

  /** Start polling for new messages. Call after auth is ready. */
  startPolling(): void {
    if (this.pollTimer) return;
    this.fetchInbox();
    this.pollTimer = setInterval(() => this.fetchInbox(), 30_000);
  }

  stopPolling(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  // ── Player list ────────────────────────────────────────────────────

  updatePlayers(userIds: Iterable<string>): void {
    this.players.clear();
    for (const id of userIds) {
      if (id !== this.selfId) {
        this.players.set(id, id.slice(0, 8));
      }
    }
    this.renderPlayers();
  }

  addPlayer(userId: string): void {
    if (userId === this.selfId) return;
    this.players.set(userId, userId.slice(0, 8));
    this.renderPlayers();
  }

  removePlayer(userId: string): void {
    this.players.delete(userId);
    this.musicState.delete(userId);
    this.voiceActive.delete(userId);
    this.renderPlayers();
  }

  setLabel(userId: string, label: string): void {
    if (this.players.has(userId)) {
      this.players.set(userId, label || userId.slice(0, 8));
      this.renderPlayers();
    }
  }

  // ── Music state ────────────────────────────────────────────────────

  setMusicPlaying(userId: string, trackName: string, playlist: string[]): void {
    if (userId === this.selfId) return;
    this.musicState.set(userId, { trackName, playlist });
    this.renderPlayers();
  }

  setMusicStopped(userId: string): void {
    this.musicState.delete(userId);
    this.renderPlayers();
  }

  getMusicStates(): Map<string, { trackName: string; playlist: string[] }> {
    return this.musicState;
  }

  // ── Voice broadcasting state ────────────────────────────────────────

  setVoiceActive(userId: string, active: boolean): void {
    const wasActive = this.voiceActive.has(userId);
    if (active) {
      this.voiceActive.add(userId);
    } else {
      this.voiceActive.delete(userId);
    }
    if (wasActive !== active) this.renderPlayers();
  }

  private renderPlayers(): void {
    this.listEl.innerHTML = "";

    const countEl = this.container.querySelector("#player-list-heading")!;
    countEl.textContent = `Online (${this.players.size})`;

    if (this.players.size === 0) {
      const empty = document.createElement("div");
      empty.textContent = "No other players";
      empty.style.cssText = "color: #666; font-size: 12px; padding: 8px 0;";
      this.listEl.appendChild(empty);
      return;
    }

    for (const [userId, label] of this.players) {
      const row = document.createElement("button");
      row.className = "player-list-row";
      row.title = `Send message to ${label}`;

      const dot = document.createElement("span");
      dot.className = "online-dot";
      row.appendChild(dot);

      const name = document.createElement("span");
      name.className = "player-name";
      name.textContent = label;
      row.appendChild(name);

      const mailIcon = document.createElement("span");
      mailIcon.className = "mail-icon";
      mailIcon.textContent = "✉";
      row.appendChild(mailIcon);

      if (this.voiceActive.has(userId)) {
        const voiceIcon = document.createElement("span");
        voiceIcon.className = "voice-icon";
        voiceIcon.textContent = "🎤";
        voiceIcon.title = "Broadcasting voice";
        row.appendChild(voiceIcon);
      }

      if (this.musicState.has(userId)) {
        const musicIcon = document.createElement("span");
        musicIcon.className = "music-icon";
        musicIcon.textContent = "♪";
        musicIcon.title = "Playing music — click to view";
        row.appendChild(musicIcon);
      }

      row.addEventListener("click", () => {
        playUIClick();
        if (this.musicState.has(userId)) {
          this.onPlayerMusicClick?.(userId, label);
        } else {
          this.openComposePanel(userId, label);
        }
      });

      this.listEl.appendChild(row);
    }
  }

  // ── Inbox ──────────────────────────────────────────────────────────

  private toggleInbox(): void {
    playUIClick();
    if (this.inboxOverlay.classList.contains("open")) {
      this.closeInbox();
    } else {
      this.openInbox();
    }
  }

  private openInbox(): void {
    this.inboxOverlay.classList.add("open");
    this.fetchInbox();
  }

  private closeInbox(): void {
    this.inboxOverlay.classList.remove("open");
    this.expandedMsgId = null;
  }

  private async fetchInbox(): Promise<void> {
    if (!activeJwt) return;

    try {
      const res = await fetch(`${config.apiUrl}/api/v1/post-box`, {
        headers: { Authorization: `Bearer ${activeJwt}` },
      });
      if (!res.ok) return;

      const data = await res.json();
      const oldUnread = this.unreadCount;
      this.messages = data.messages ?? [];
      this.unreadCount = data.unread_count ?? 0;

      this.updateBadge();
      if (this.inboxOverlay.classList.contains("open")) {
        this.renderInbox();
      }

      // Play sound if new unread messages appeared
      if (this.unreadCount > oldUnread && oldUnread >= 0) {
        playNewMail();
      }
    } catch {
      // silent fail — will retry on next poll
    }
  }

  private updateBadge(): void {
    if (this.unreadCount > 0) {
      this.postboxBadge.textContent = String(this.unreadCount);
      this.postboxBadge.classList.add("visible");
      this.postboxBtn.classList.add("has-unread");
    } else {
      this.postboxBadge.classList.remove("visible");
      this.postboxBtn.classList.remove("has-unread");
    }
  }

  private renderInbox(): void {
    this.inboxMessages.innerHTML = "";

    if (this.messages.length === 0) {
      this.inboxMessages.appendChild(this.inboxEmpty.cloneNode(true));
      return;
    }

    for (const msg of this.messages) {
      const el = document.createElement("div");
      el.className = "inbox-msg" + (msg.read_at ? "" : " unread") + (this.expandedMsgId === msg.id ? " expanded" : "");

      const header = document.createElement("div");
      header.className = "inbox-msg-header";

      const subject = document.createElement("span");
      subject.className = "inbox-msg-subject";
      subject.textContent = msg.subject;
      header.appendChild(subject);

      const date = document.createElement("span");
      date.className = "inbox-msg-date";
      date.textContent = this.formatDate(msg.sent_at);
      header.appendChild(date);

      el.appendChild(header);

      if (msg.sender_id) {
        const from = document.createElement("div");
        from.className = "inbox-msg-from";
        const senderLabel = this.players.get(msg.sender_id) || msg.sender_id.slice(0, 8);
        from.textContent = `From: ${senderLabel}`;
        el.appendChild(from);
      } else {
        const from = document.createElement("div");
        from.className = "inbox-msg-from";
        from.textContent = `System · ${msg.kind.replace(/_/g, " ")}`;
        el.appendChild(from);
      }

      const body = document.createElement("div");
      body.className = "inbox-msg-body";
      body.textContent = msg.body;
      el.appendChild(body);

      // Action buttons (visible when expanded)
      const actions = document.createElement("div");
      actions.className = "inbox-msg-actions";

      const archiveBtn = document.createElement("button");
      archiveBtn.className = "inbox-archive-btn";
      archiveBtn.textContent = "Archive";
      archiveBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        this.archiveMessage(msg.id);
      });
      actions.appendChild(archiveBtn);

      if (msg.sender_id) {
        const replyBtn = document.createElement("button");
        replyBtn.className = "inbox-reply-btn";
        replyBtn.textContent = "Reply";
        replyBtn.addEventListener("click", (e) => {
          e.stopPropagation();
          const label = this.players.get(msg.sender_id!) || msg.sender_id!.slice(0, 8);
          this.closeInbox();
          this.openComposePanel(msg.sender_id!, label);
          this.panelSubject.value = msg.subject.startsWith("Re: ") ? msg.subject : `Re: ${msg.subject}`;
        });
        actions.appendChild(replyBtn);
      }

      el.appendChild(actions);

      el.addEventListener("click", () => {
        if (this.expandedMsgId === msg.id) {
          this.expandedMsgId = null;
        } else {
          this.expandedMsgId = msg.id;
          if (!msg.read_at) this.markRead(msg.id);
        }
        this.renderInbox();
      });

      this.inboxMessages.appendChild(el);
    }
  }

  private formatDate(iso: string): string {
    try {
      const d = new Date(iso);
      const now = new Date();
      if (d.toDateString() === now.toDateString()) {
        return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      }
      return d.toLocaleDateString([], { month: "short", day: "numeric" });
    } catch {
      return "";
    }
  }

  private async markRead(messageId: string): Promise<void> {
    if (!activeJwt) return;
    try {
      await fetch(`${config.apiUrl}/api/v1/post-box/${encodeURIComponent(messageId)}/read`, {
        method: "POST",
        headers: { Authorization: `Bearer ${activeJwt}` },
      });
      const msg = this.messages.find((m) => m.id === messageId);
      if (msg) msg.read_at = new Date().toISOString();
      if (this.unreadCount > 0) this.unreadCount--;
      this.updateBadge();
    } catch { /* silent */ }
  }

  private async archiveMessage(messageId: string): Promise<void> {
    if (!activeJwt) return;
    try {
      await fetch(`${config.apiUrl}/api/v1/post-box/${encodeURIComponent(messageId)}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${activeJwt}` },
      });
      this.messages = this.messages.filter((m) => m.id !== messageId);
      if (this.expandedMsgId === messageId) this.expandedMsgId = null;
      this.fetchInbox();
    } catch { /* silent */ }
  }

  // ── Compose panel ──────────────────────────────────────────────────

  private openComposePanel(recipientId: string, label: string): void {
    this.selectedRecipient = recipientId;
    this.panelRecipient.textContent = label;
    if (!this.panelSubject.value) this.panelSubject.value = "";
    this.panelBody.value = "";
    this.panelStatus.textContent = "";
    this.panelStatus.className = "postbox-status";
    this.panelSendBtn.disabled = false;
    this.panel.classList.add("open");
    this.panelSubject.focus();
  }

  private closeComposePanel(): void {
    this.panel.classList.remove("open");
    this.selectedRecipient = "";
    this.panelSubject.value = "";
  }

  private async sendMessage(): Promise<void> {
    const subject = this.panelSubject.value.trim();
    const body = this.panelBody.value.trim();

    if (!subject || !body) {
      this.panelStatus.textContent = "Subject and message are required";
      this.panelStatus.className = "postbox-status error";
      return;
    }

    if (!this.selectedRecipient) return;

    this.panelSendBtn.disabled = true;
    this.panelStatus.textContent = "Sending…";
    this.panelStatus.className = "postbox-status";

    try {
      const res = await fetch(`${config.apiUrl}/api/v1/post-box/send`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${activeJwt}`,
        },
        body: JSON.stringify({
          recipient_id: this.selectedRecipient,
          subject,
          body,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: { code: "unknown" } }));
        throw new Error(err.error?.code ?? "failed");
      }

      this.panelStatus.textContent = "Message sent!";
      this.panelStatus.className = "postbox-status success";
      this.panelSubject.value = "";
      this.panelBody.value = "";

      setTimeout(() => this.closeComposePanel(), 1200);
    } catch (err) {
      this.panelStatus.textContent = `Failed: ${err instanceof Error ? err.message : "unknown error"}`;
      this.panelStatus.className = "postbox-status error";
      this.panelSendBtn.disabled = false;
    }
  }
}
